import {action, makeObservable, observable, runInAction} from 'mobx';

import type {LocationType, LocationWithFromState} from '../../types';

import {Crumb} from './Crumb.store';

export class BreadcrumbsStore {
  crumbs: Crumb[];
  location: LocationType | LocationWithFromState;
  private readonly isWithCrumbs: boolean;
  private readonly isWithFrom: boolean;
  private skipRoot: boolean;

  constructor(config: {
    isWithCrumbs: boolean;
    isWithFrom: boolean;
    skipRoot?: boolean;
  }) {
    this.isWithCrumbs = config.isWithCrumbs;
    this.isWithFrom = config.isWithFrom;
    this.skipRoot = config.skipRoot ?? true;
    this.crumbs = [];

    makeObservable(this, {
      crumbs: observable,
      location: observable,
      setCrumbs: action,
    });
  }

  nextLocation = async (location: LocationType) => {
    if (this.location?.pathname !== location.pathname) {
      this.location = location;

      const crumbs = await this.createCrumbs();

      runInAction(() => {
        this.setCrumbs(crumbs);
      });
    }
  };
  setCrumbs = (crumbs: Crumb[]) => {
    this.crumbs = crumbs;
  };
  setSkipRoot = (skipRoot: boolean) => {
    this.skipRoot = skipRoot;
  };

  private async createCrumbs() {
    const crumbs: Crumb[] = [];

    if (!this.isWithCrumbs) {
      return crumbs;
    }

    if (
      this.isWithFrom &&
      (this.location as LocationWithFromState).state?.from
    ) {
      const fromRoot = (this.location as LocationWithFromState).state?.from
        .split('/')
        .filter(Boolean)[0];

      if (fromRoot) {
        const crumb = await Crumb.build([fromRoot]);

        crumbs.push(crumb);
      }
    }

    if (this.location.pathname) {
      const pathChunks = this.location.pathname.split('/').filter(Boolean);

      const shouldSkipFirst =
        this.skipRoot || pathChunks[0] === crumbs[0]?._label;
      const promises = pathChunks
        /** slice(1) to skip root chunk */
        .slice(shouldSkipFirst ? 1 : 0)
        .map((_, index) => {
          return Crumb.build(
            pathChunks.slice(0, shouldSkipFirst ? index + 2 : index + 1),
          );
        });

      const pathCrumbs = await Promise.all(promises);

      crumbs.push(...pathCrumbs);
    }

    return crumbs;
  }
}
